/*
 * � 2025, NTT Data Group. All rights reserved. See LICENSE.txt file in the project root for third party licenses info.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */
import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { delay, Observable } from "rxjs";
import { HttpMethods } from "../models/ui/http-methods.model";
import { HttpRequestOptions } from "../models/ui/http-request-options.model";
import { APIS } from "../utils/apis/apis";
import { getEndpointWithPathParams, getUrl } from "../utils/utils";

@Injectable({ providedIn: "root" })
export class CommunicationService {
	constructor(private _http: HttpClient) {}

	/**
	 * Calls a specified service endpoint with the given HTTP method and options.
	 *
	 * @param service - The service endpoint to call, specified as a string in the format "first/second".
	 * @param method - The HTTP method to use for the request. Defaults to HttpMethods.GET.
	 * @param options - The options for the HTTP request, including headers, context, params, etc.
	 * @param body - The body of the request, if applicable (for POST and PUT methods).
	 * @returns An Observable of the response from the HTTP request.
	 */

	public callService(
		service: string,
		method: HttpMethods = HttpMethods.GET,
		options: HttpRequestOptions = {},
		body?: unknown,
		isVersioningNeeded: boolean = true
	): Observable<any> {
		const simulationDelay: number = Math.floor(Math.random() * APIS.moduleDefaults.delay);
		const [first, second]: string[] = service.split("/");
		const configEntry = APIS.apis[first][second];
		const isAvailable = !APIS.moduleDefaults.isMocked && !configEntry.isMocked;
		const endpoint: string = getEndpointWithPathParams(
			configEntry.endpoint,
			options.pathParams
		);
		const url = getUrl(endpoint, isVersioningNeeded);
		const requestOptions: HttpRequestOptions = {
			headers: options.headers,
			context: options.context,
			params: options.params,
			pathParams: options.pathParams,
			reportProgress: options.reportProgress,
			responseType: options.responseType,
			withCredentials: true,
			body
		};

		return isAvailable
			? this._http.request(method, url, requestOptions)
			: configEntry.mock(requestOptions).pipe(delay(simulationDelay));
	}
}
